import Head from "next/head";
import BusinessRegister from "@/components/register/BusinessRegister";
import DownloadApp from "@/components/shared/DownloadApp";
import Loading from "@/components/shared/Loading";
import PageBanner from "@/components/ui/PageBanner";
import tenureItems, { TenureItem } from "@/core/constants/tenure";
import { GET_PACKAGE_LIST } from "@/core/graphql/queries/getPackageList";
import { Package } from "@/core/models/packages/package";
import { initializeApollo } from "@/lib/apolloClient";
import { slugify } from "@/lib/slugify";
import { useRouter } from "next/router";

interface Props {
    packageList: Package[];
}

export default function ListBusinessPage({ packageList }: Props) {
    const router = useRouter();
    const { query, isReady } = router;

    if (!isReady) return <Loading />;

    const packageName = query.package as string;
    const tenure = query.tenure as string;

    // Check if packageName and tenure exist before proceeding
    if (!packageName || !tenure) {
        router.push('/pricing');
        return <Loading />; // Return loading while redirecting
    }

    // Add null checks and default values for slugify
    const selectedPackage = packageList.find(pkg => 
        pkg.packageName && slugify(pkg.packageName || '') === slugify(packageName)
    );
    const selectedTenure = tenureItems.find(t => 
        t.label && slugify(t.label || '') === slugify(tenure)
    );

    if (!selectedPackage || !selectedTenure) {
        router.push('/pricing');
        return <Loading />; // Return loading while redirecting
    }

    return (
        <>
            <Head>
                <title>List Your Automotive Business | Register as a Car Parts Supplier | CarSparesEzyFind</title>
                <meta name="description" content="List your automotive business on CarSparesEzyFind and reach thousands of car owners across South Africa. Register your auto parts store, spares dealership, or automotive service provider to start selling car parts and accessories online today." />
                <meta name="keywords" content="list auto business South Africa, register car parts supplier, automotive business listing, sell car spares online, auto parts dealer registration, CarSparesEzyFind business" />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="List Your Automotive Business | CarSparesEzyFind" />
                <meta property="og:description" content="Register your automotive business and reach thousands of car owners across South Africa. Sell car parts, spares, and automotive services on CarSparesEzyFind." />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="CarSparesEzyFind" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="List Your Automotive Business | CarSparesEzyFind" />
                <meta name="twitter:description" content="Register your automotive business and reach thousands of car owners across South Africa. Sell car parts, spares, and automotive services." />
            </Head>
            <PageBanner backgroundImage='/images/register_banner.webp' title='Register Business' />
            <div className="container">
                <BusinessRegister selectedPackage={selectedPackage} selectedTenure={selectedTenure} />
            </div>
            <DownloadApp />
        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();

    const { data: packageData } = await apolloClient.query({
            query: GET_PACKAGE_LIST,
            variables: { status: true },
        });

    return {
        props: {
            packageList: packageData?.getMstPackageList?.result || []
        }
    };
};